import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Spin, Space, Modal, message, Popconfirm } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, gql, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import {
  MinusSquareOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { activePeriod, disabledDate } from '../../utils/date';
import DataGrid from '../../components/DataGrid';
import * as Config from '../../appConfig';

import {
  updateTableMetaData,
  updateGridMetaData,
  resetGridFilters,
} from '../../store/reducers/expenseSlice';
import { ADVANCES_LIST_FIELDS } from '../../apollo/fragments';
import { EMPLOYEE_LOOKUP_QUERY } from '../../apollo/queries';
import {
  ADD_ADVANCE_MUTATION,
  UPDATE_ADVANCE_MUTATION,
  DELETE_ADVANCE_MUTATION,
  ADD_ADVANCE_DEDUCTION_MUTATION,
  UPDATE_ADVANCE_DEDUCTION_MUTATION,
  DELETE_ADVANCE_DEDUCTION_MUTATION,
} from '../../apollo/mutations';

import AddAdvanceForm from '../../components/Expense/Forms/AddAdvanceForm';
import AddAdvanceDeductionForm from '../../components/Expense/Forms/AddAdvanceDeductionForm';
import ProfileCard from '../../components/Employee/ProfileCard';
import AdvanceDeductions from './AdvanceDeductions';
import useDeductionsQuery from './useDeductionsQuery';

const ADVANCES_QUERY = gql`
  ${ADVANCES_LIST_FIELDS}
  query Advances(
    $first: Int
    $page: Int
    $filters: [QueryGetAdvancesFilter]
    $sort: QueryGetAdvancesSorter
  ) {
    advances(first: $first, page: $page, filters: $filters, sort: $sort) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...AdvanceListFields
      }
    }
  }
`;

const Advances = ({ gridId }) => {
  const { metaData, grids } = useSelector((state) => state.expense);
  const {
    user: { ability },
  } = useSelector((state) => state.login);
  const dispatch = useDispatch();

  const grid = grids.find((i) => i.id === gridId);

  const [showAddAdvanceModal, setShowAddAdvanceModal] = useState(false);
  const [showAdvanceDeductModal, setShowAdvanceDeductModal] = useState(false);
  const [activeAdvance, setActiveAdvance] = useState(null);
  const [activeAdvanceId, setActiveAdvanceId] = useState(null);
  const [activeAdvanceDeduction, setActiveAdvanceDeduction] = useState(null);

  const [freezePrevMonth, currentMonth, prevMonth] = activePeriod();

  const { refetchDeductions } = useDeductionsQuery(activeAdvanceId);

  const queryVariables = useMemo(() => {
    return {
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
      sort: grid.metaData.sorting,
    };
  }, [
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
    grid.metaData.sorting,
  ]);

  const { loading, data, error, refetch } = useQuery(ADVANCES_QUERY, {
    variables: queryVariables,
  });

  console.log('ERROR', error);

  const [
    lookupEmployee,
    { data: lookupData, loading: fetching },
  ] = useLazyQuery(EMPLOYEE_LOOKUP_QUERY);

  const [addAdvance, { loading: adding }] = useMutation(ADD_ADVANCE_MUTATION);
  const [updateAdvance, { loading: updating }] = useMutation(
    UPDATE_ADVANCE_MUTATION
  );
  const [deleteAdvance, { loading: deleting }] = useMutation(
    DELETE_ADVANCE_MUTATION
  );

  const [addAdvanceDeduction, { loading: adding2 }] = useMutation(
    ADD_ADVANCE_DEDUCTION_MUTATION
  );
  const [updateAdvanceDeduction, { loading: updating2 }] = useMutation(
    UPDATE_ADVANCE_DEDUCTION_MUTATION
  );
  const [deleteAdvanceDeduction, { loading: deleting2 }] = useMutation(
    DELETE_ADVANCE_DEDUCTION_MUTATION
  );

  const onEmployeeLookup = (search) => {
    lookupEmployee({
      variables: {
        search,
        status: 'active',
      },
    });
  };

  const columns = [
    {
      title: 'Sl.No',
      dataIndex: 'id',
      key: 'id',
      serverKey: 'ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      className: 'width-100',
    },
    {
      title: 'EMP ID',
      dataIndex: ['employee', 'emp_id'],
      key: 'emp_id',
      serverKey: 'EMP_ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      className: 'width-150',
      render: (value, record) => {
        return <ProfileCard employee={record.employee} />;
      },
    },
    {
      title: 'Name',
      dataIndex: ['employee', 'first_name'],
      key: 'first_name',
      serverKey: 'NAME',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      render: (value, record) => {
        return (
          record && `${record.employee.first_name} ${record.employee.last_name}`
        );
      },
    },
    {
      title: 'For Month',
      dataIndex: 'for_month',
      key: 'for_month',
      serverKey: 'MONTH',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'date',
      format: 'month',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      serverKey: 'AMOUNT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
    // {
    //   title: 'Returned Amount',
    //   dataIndex: 'returned_amount',
    //   key: 'returned_amount',
    //   serverKey: 'RETURNED_AMOUNT',
    //   sorter: true,
    //   canExclude: false,
    //   visible: true,
    //   ellipsis: false,
    //   dataType: 'string',
    //   format: 'currency',
    //   className: 'cell-currency',
    // },
    // {
    //   title: 'Pending Amount',
    //   dataIndex: 'pending_amount',
    //   key: 'pending_amount',
    //   serverKey: 'PENDING_AMOUNT',
    //   sorter: true,
    //   canExclude: false,
    //   visible: true,
    //   ellipsis: false,
    //   dataType: 'string',
    //   format: 'currency',
    //   className: 'cell-currency',
    // },
    // {
    //   title: 'Current Deduction',
    //   dataIndex: 'current_deduction',
    //   key: 'current_deduction',
    //   serverKey: 'CURRENT_DEDUCTION',
    //   sorter: true,
    //   canExclude: false,
    //   visible: true,
    //   ellipsis: false,
    //   dataType: 'string',
    //   format: 'currency',
    //   className: 'cell-currency',
    // },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      serverKey: 'NOTE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Created By',
      dataIndex: ['createdBy', 'name'],
      key: 'created_by',
      serverKey: 'USER',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'string',
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      serverKey: 'CREATED_AT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'date',
      format: 'date',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      serverKey: 'ACTIONS',
      sorter: false,
      canExclude: false,
      visible: true,
      ellipsis: false,
      disableSearch: true,
      className: 'width-175',
      render: (value, record) => {
        const for_month = moment(record.for_month);
        const isEditable = disabledDate(
          for_month,
          freezePrevMonth,
          currentMonth,
          prevMonth
        );
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<MinusSquareOutlined />}
              title="Deduct"
              onClick={() => {
                setActiveAdvanceId(record.id);
                setShowAdvanceDeductModal(true);
              }}
            >
              Deduct
            </Button>
            {ability.toEditAdvance && (
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
                title="Edit"
                onClick={() => {
                  setActiveAdvance(record);
                  setShowAddAdvanceModal(true);
                }}
                disabled={isEditable}
              ></Button>
            )}
            {ability.toDeleteAdvance && (
              <Popconfirm
                title="Are you sure to delete this?"
                onConfirm={() => {
                  onDeleteConfirm(record);
                }}
                onCancel={() => {}}
                okText="Delete"
                cancelText="No"
                placement="rightTop"
                disabled={isEditable}
              >
                <Button
                  type="ghost"
                  size="small"
                  icon={<DeleteOutlined />}
                  title="Delete"
                  danger
                  disabled={isEditable}
                ></Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    dispatch(
      updateTableMetaData({
        metaData: {
          ...metaData,
          ...updatedMetaData,
        },
      })
    );
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  const onSuccessMutation = (mesageText = 'Details updated successfully!') => {
    refetch(queryVariables);
    message.success(mesageText);
  };

  const onAdvanceSubmit = (values, callBack) => {
    console.log('onAdvanceSubmit called with///', values);
    const isUpdate = values.id && values.id !== null;
    const variables = {
      id: isUpdate ? parseInt(values.id, 10) : values.employee.value,
      input: {
        for_month: values.for_month
          ? values.for_month.startOf('month').format(Config.DATE_FORMAT_SERVER)
          : null,
        amount: values.amount ? values.amount : '',
        note: values.note ? values.note : '',
      },
    };
    const mutationToCall = isUpdate ? updateAdvance : addAdvance;
    mutationToCall({
      variables,
    })
      .then(({ data }) => {
        const advance = isUpdate ? data.updateAdvance : data.addAdvance;
        console.log('onAdvanceSubmit finished', advance);
        if (advance) {
          callBack({ status: true });
          onSuccessMutation();
          setShowAddAdvanceModal(false);
        }
        // setActiveLeave(null);
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
        callBack({ status: false });
      });
  };

  const onDeleteConfirm = (advance) => {
    console.log('onDeleteConfirm called with///', advance);
    deleteAdvance({
      variables: {
        id: advance.id,
      },
    })
      .then(({ data: { deleteAdvance } }) => {
        console.log('onDeleteConfirm finished', deleteAdvance);
        if (deleteAdvance.id) {
          onSuccessMutation('Advance deleted!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onAdvanceDeductionSubmit = (values) => {
    console.log('onAdvanceDeductionSubmit called with///', values);
    const isUpdate = values.id && values.id !== null;
    const variables = {
      id: isUpdate ? parseInt(values.id, 10) : values.advance_id,
      input: {
        for_month: values.for_month
          ? values.for_month.startOf('month').format(Config.DATE_FORMAT_SERVER)
          : null,
        amount: values.amount ? values.amount : '',
        note: values.note ? values.note : '',
      },
    };
    const mutationToCall = isUpdate
      ? updateAdvanceDeduction
      : addAdvanceDeduction;
    mutationToCall({
      variables,
    })
      .then(({ data }) => {
        const advanceDeduction = isUpdate
          ? data.updateAdvanceDeduction
          : data.addAdvanceDeduction;
        console.log('onAdvanceDeductionSubmit finished', advanceDeduction);
        if (advanceDeduction) {
          refetchDeductions(advanceDeduction.advance_id);
          onSuccessMutation();
          setShowAdvanceDeductModal(false);
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onAdvanceDeductDeleteConfirm = (advanceDeduction) => {
    console.log(
      'onAdvanceDeductDeleteConfirm called with///',
      advanceDeduction
    );
    setActiveAdvanceId(advanceDeduction.advance_id);
    deleteAdvanceDeduction({
      variables: {
        id: advanceDeduction.id,
      },
    })
      .then(({ data: { deleteAdvanceDeduction } }) => {
        console.log(
          'onAdvanceDeductDeleteConfirm finished',
          deleteAdvanceDeduction
        );
        if (deleteAdvanceDeduction.id) {
          refetchDeductions(deleteAdvanceDeduction.advance_id);
          onSuccessMutation('Advance deduction deleted!');
        }
        setActiveAdvance(null);
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onEditDeduction = (deduction) => {
    setActiveAdvanceId(deduction.advance_id);
    setActiveAdvanceDeduction(deduction);
    setShowAdvanceDeductModal(true);
  };

  const onCancelAddEditDeduction = () => {
    setActiveAdvanceId(null);
    setActiveAdvanceDeduction(null);
    setShowAdvanceDeductModal(false);
  };

  return (
    <Spin spinning={loading}>
      <Card className="employeesCard" style={{ minHeight: '200px' }}>
        {/* {error && <Error message={error.message} />} */}

        {data && (
          <DataGrid
            dataSource={data.advances.data}
            columns={columns}
            filters={grid.metaData.filters}
            visibleColumns={grid.metaData.visibleColumns}
            sorting={grid.metaData.sorting}
            pagination={grid.metaData.pagination}
            onChange={onChange}
            totalRecords={data.advances.paginatorInfo.total}
            onClearFilter={() => {
              dispatch(resetGridFilters({ gridId }));
            }}
            onAddNewEntry={() => {
              console.log('Add new entry clicked!');
              setShowAddAdvanceModal(true);
            }}
            addNewEntryBtnLabel="Add Advance"
            expandable={{
              expandedRowClassName: (record, index, indent) => 'row-expanded',
              expandedRowRender: (record) => (
                <AdvanceDeductions
                  advance={record}
                  onEditDeduction={onEditDeduction}
                  onDeleteConfirm={onAdvanceDeductDeleteConfirm}
                />
              ),
              rowExpandable: (record) =>
                record.deductions && record.deductions.length > 0,
            }}
          />
        )}
      </Card>
      <Modal
        title={activeAdvance ? 'Edit Advance' : 'Add Advance'}
        visible={showAddAdvanceModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={() => setShowAddAdvanceModal(false)}
        maskClosable={false}
      >
        <Spin spinning={adding || updating || deleting}>
          <AddAdvanceForm
            advance={activeAdvance}
            lookupData={
              lookupData && lookupData.employeeLookup
                ? lookupData.employeeLookup.map((i) => {
                    return {
                      label: `${i.first_name} ${i.last_name} (${i.emp_id})`,
                      value: i.id,
                    };
                  })
                : []
            }
            onLookup={onEmployeeLookup}
            onAdvanceSubmit={onAdvanceSubmit}
            onCancelClick={() => setShowAddAdvanceModal(false)}
            fetching={fetching}
          />
        </Spin>
      </Modal>
      <Modal
        title="Deduct Advance from Employee"
        visible={showAdvanceDeductModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={() => onCancelAddEditDeduction()}
        maskClosable={false}
      >
        <Spin spinning={adding2 || updating2 || deleting2}>
          <AddAdvanceDeductionForm
            advance_id={activeAdvanceId}
            deduction={activeAdvanceDeduction}
            onAdvanceDeductionSubmit={onAdvanceDeductionSubmit}
            onCancelClick={() => onCancelAddEditDeduction()}
          />
        </Spin>
      </Modal>
    </Spin>
  );
};

export default Advances;
